import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { Platform } from 'react-native';
import { useAppStore, selectUILocale } from '../store/appStore';
import { isRTLLocale } from '../utils/rtl';
import { createTranslator, TranslationKey } from '../i18n';
import { typography } from '../theme/typography';
import { fonts } from '../utils/fonts';
import { getFontConfig } from '../config/font.config';

type Translator = (key: TranslationKey, params?: Record<string, string | number>) => string;

interface FontFamily {
  thin?: string;
  primary?: string;
  medium: string;
  semibold: string;
  bold: string;
  black: string;
}

interface LocaleContextType {
  locale: string;
  isRTL: boolean;
  t: Translator;
  fontFamily: FontFamily;
  /** Physical start edge for current direction — 'left' in LTR, 'right' in RTL */
  start: 'left' | 'right';
  /** Physical end edge for current direction — 'right' in LTR, 'left' in RTL */
  end: 'left' | 'right';
}

const LocaleContext = createContext<LocaleContextType | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const locale = useAppStore(selectUILocale);
  const isRTL = isRTLLocale(locale);

  // Inject dir attribute on web so CSS logical properties and scrollbars work for free
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = locale.split('_')[0];
    }
  }, [isRTL, locale]);

  const value = useMemo<LocaleContextType>(() => {
    // Web: use CSS font-family stacks (with fallbacks)
    // Native: use exact registered font names (no commas — RN doesn't support stacks)
    const isNative = Platform.OS !== 'web';
    const { primaryFamily, arabicFamily } = getFontConfig();
    const nativeArabicFonts = fonts[arabicFamily];
    const nativePrimaryFonts = fonts[primaryFamily];
    const fontFamily: FontFamily = isRTL
      ? {
          thin:     isNative ? (('thin' in nativeArabicFonts) ? nativeArabicFonts.thin : nativeArabicFonts.regular) : typography.fontFamily.arabicThin,
          primary:  isNative ? nativeArabicFonts.regular   : typography.fontFamily.arabic,
          medium:   isNative ? nativeArabicFonts.medium    : typography.fontFamily.arabicMedium,
          semibold: isNative ? nativeArabicFonts.semibold  : typography.fontFamily.arabicSemibold,
          bold:     isNative ? nativeArabicFonts.bold      : typography.fontFamily.arabicBold,
          black:    isNative ? nativeArabicFonts.black     : typography.fontFamily.arabicBlack,
        }
      : {
          thin:     isNative ? nativePrimaryFonts.regular  : typography.fontFamily.primary,
          primary:  isNative ? nativePrimaryFonts.regular  : typography.fontFamily.primary,
          medium:   isNative ? nativePrimaryFonts.medium   : typography.fontFamily.medium,
          semibold: isNative ? nativePrimaryFonts.semibold : typography.fontFamily.semibold,
          bold:     isNative ? nativePrimaryFonts.bold     : typography.fontFamily.bold,
          black:    isNative ? nativePrimaryFonts.black    : typography.fontFamily.black,
        };

    return {
      locale,
      isRTL,
      t: createTranslator(locale),
      fontFamily,
      start: isRTL ? 'right' : 'left',
      end: isRTL ? 'left' : 'right',
    };
  }, [locale, isRTL]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

const defaultLocaleContext: LocaleContextType = {
  locale: 'en',
  isRTL: false,
  t: (key) => key,
  fontFamily: {
    thin:     typography.fontFamily.primary,
    primary:  typography.fontFamily.primary,
    medium:   typography.fontFamily.medium,
    semibold: typography.fontFamily.semibold,
    bold:     typography.fontFamily.bold,
    black:    typography.fontFamily.black,
  },
  start: 'left',
  end: 'right',
};

export function useLocale(): LocaleContextType {
  const ctx = useContext(LocaleContext);
  return ctx ?? defaultLocaleContext;
}
